import { useState } from "react";
import { If } from "../Conditional";
import { Card, CardContent, CardHeader } from "~/components/v3/shadcn/Card";
import { Button } from "../themed/Button";
import ThemedLink from "../themed/ThemedLink";
import UserLink from "../users-components/UserLink";
import EditMoreLinks from "./CommunityPeopleAndMore/EditMoreLinks";
import EditOrganizers from "./CommunityPeopleAndMore/EditOrganizers";
import EditTopHosts from "./CommunityPeopleAndMore/EditTopHosts";
import { CommunityPage } from "~/types/CommunityTypes";
import { ProductCarousel } from "~/components/merchandise-components/ProductList";
import Link from "next/link";

import { LuChevronRight } from "react-icons/lu";
import { BiCrown } from "react-icons/bi";
import { Label } from "../shadcn/Label";
import Image from "next/image";
import { GetCommunity } from "~/app/v1/[communitySlug]/actions";
import { cn } from "~/utils/fontsConfig";
import { FollowUserButton } from "~/components/v4/themed/user/FollowUserButton";
import { HostTooltip } from "../events-components/HostedByV3";
import { UserAvatar } from "~/components/v4/shadcn/UserAvatar";

const EditMode = ({
    community,
    refetch,
    hasPermissions,
    setMode,
}: {
    refetch?: () => void;
    community: CommunityPage;
    hasPermissions: boolean;
    setMode: (mode: "view" | "edit") => void;
}) => {
    return (
        <Card className="min-w-[265px] max-w-[90vw] p-0 md:max-w-[350px] md:border-none md:bg-transparent">
            <CardContent className="flex flex-col gap-9 p-2 pt-4">
                <div className="flex flex-col gap-2">
                    <Button
                        className="rounded"
                        variant="secondary"
                        onClick={() => {
                            setMode("view");
                        }}
                    >
                        Done
                    </Button>
                </div>
                <div className="flex flex-col gap-2">
                    <EditOrganizers community={community} refetch={refetch} />

                    <EditTopHosts community={community} refetch={refetch} />

                    <EditMoreLinks community={community} refetch={refetch} />
                </div>
            </CardContent>
        </Card>
    );
};

const ViewMode = ({
    community,
    hasPermissions,
    setMode,
}: {
    community: GetCommunity;
    hasPermissions: boolean;
    setMode: (mode: "view" | "edit") => void;
}) => {
    const communityLinks = community?.links.filter(
        (l) => l.type === "COMMUNITY" || l.type === "OTHER",
    );

    // This is a trial way to show the top hosts, let's see what community managers think of it
    // const { data: topHosts } = api.community.getCommunityTop5Hosts.useQuery(
    //     { communitySlug: community.slug },
    //     { refetchOnMount: false },
    // );

    return (
        <div className="relative mt-8 flex w-full min-w-[265px] items-center justify-center md:block lg:mt-0 lg:max-w-[300px]">
            <Card className="sticky top-[80px] w-full p-0 md:border-none md:bg-transparent">
                <If condition={hasPermissions}>
                    <div className=" mb-2 flex w-full justify-center">
                        <Button
                            className="rounded"
                            variant="secondary"
                            onClick={() => {
                                setMode("edit");
                            }}
                        >
                            Edit people and links
                        </Button>
                    </div>
                </If>
                <CardContent className="flex flex-col gap-4 p-2 px-4 md:gap-4 md:p-0 md:px-0">
                    <If condition={!!community.leaders?.length}>
                        <div className="flex flex-col gap-4 bg-v3-dark-gray md:gap-6 md:rounded-lg md:p-4">
                            <Label>Organizers</Label>
                            {community.leaders?.map((h) => (
                                <div
                                    className="flex w-full items-center gap-4"
                                    key={`organizer-host-${h.id}`}
                                >
                                    <span className="flex items-center gap-2">
                                        <HostTooltip
                                            user={h}
                                            triggerClassName="flex items-center gap-2"
                                        >
                                            <UserAvatar
                                                user={h}
                                                className="h-10 w-10"
                                            />
                                            <p className="text-md line-clamp-1 text-left font-medium">
                                                {h.name}
                                            </p>
                                        </HostTooltip>
                                    </span>

                                    <FollowUserButton
                                        userId={h.id}
                                        isFollowing={h.isFollowing}
                                        className="ml-auto h-6 rounded-md px-2 py-1"
                                    />
                                </div>
                            ))}
                        </div>
                    </If>
                    <If
                        condition={
                            (community.isTopHostsUsingLeaderboard &&
                                !!community.leaderboard.length) ||
                            (!community.isTopHostsUsingLeaderboard &&
                                !!community.topHosts.length)
                        }
                    >
                        <div className="flex flex-col gap-2 bg-v3-dark-gray md:rounded-lg md:p-4">
                            <Link
                                href={`${community.slug}/leaderboard`}
                                className="group flex  cursor-pointer flex-col justify-center gap-2 rounded-lg p-2 hover:bg-v3-N-500"
                            >
                                <div className="flex items-center gap-2">
                                    <BiCrown className="h-6 w-6 fill-v3-primary" />
                                    <Label>Top Hosts</Label>
                                </div>
                                <If
                                    condition={
                                        community.isTopHostsUsingLeaderboard
                                    }
                                >
                                    <p className="flex items-center">
                                        Go to Leaderboard <LuChevronRight />
                                    </p>
                                </If>
                            </Link>
                            <If
                                condition={
                                    !community.isTopHostsUsingLeaderboard
                                }
                            >
                                {/* Show manually inserted top hosts */}

                                {community.topHosts?.map((h) => (
                                    <div
                                        className="flex w-full items-center gap-4"
                                        key={`top-host-${h.id}`}
                                    >
                                        <UserLink
                                            user={h}
                                            className="max-w-[170px]"
                                        />
                                        <FollowUserButton
                                            userId={h.id}
                                            isFollowing={h.isFollowing}
                                        />
                                    </div>
                                ))}
                            </If>
                            <If
                                condition={community.isTopHostsUsingLeaderboard}
                            >
                                {community.leaderboard?.map((h) => (
                                    <div
                                        className="flex w-full items-center gap-4"
                                        key={`top-host-${h.id}`}
                                    >
                                        <UserLink
                                            user={h}
                                            className="max-w-[170px]"
                                        />
                                        <FollowUserButton
                                            userId={h.id}
                                            isFollowing={h.isFollowing}
                                        />
                                    </div>
                                ))}
                            </If>
                        </div>
                    </If>

                    <If condition={!!communityLinks?.length}>
                        <div className="flex flex-col gap-2 bg-v3-dark-gray md:gap-6 md:rounded-lg md:p-4">
                            <Label>More</Label>
                            <div className="line-clamp-1 flex w-full flex-col gap-2">
                                {communityLinks.map((l, i) => (
                                    <Link
                                        href={l.url}
                                        className="flex justify-between rounded bg-v3-neutral p-2 hover:bg-v3-N-400"
                                        key={"more-links-" + i}
                                    >
                                        {l.name}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </If>

                    <div className="flex w-full flex-col gap-2">
                        <ProductCarousel
                            className="max-w-full"
                            communitySlug={community?.slug}
                            displayAffiliateProducts={
                                community?.displayAffiliateProducts
                            }
                        />
                    </div>

                    <div className="flex w-full flex-col items-center gap-2 lg:hidden">
                        {community.partnerTier
                            .sort((a, b) => a.order - b.order)
                            .map((partnerTier) => {
                                if (!partnerTier.partners.length) return null;
                                return (
                                    <Card
                                        key={partnerTier.id}
                                        className="w-full"
                                    >
                                        <CardHeader className="px-0">
                                            <Label>{partnerTier.name}</Label>
                                        </CardHeader>
                                        <CardContent
                                            className={cn(
                                                "p-0",
                                                partnerTier.renderType ===
                                                    "GRID"
                                                    ? "grid grid-cols-2 gap-4"
                                                    : "flex flex-col gap-4",
                                            )}
                                        >
                                            {partnerTier.partners.map((p) => {
                                                return (
                                                    <Card
                                                        className="flex h-[100px] w-full gap-2 bg-v3-N-500 p-3"
                                                        key={p.id}
                                                    >
                                                        <Link
                                                            href={p.url}
                                                            target="_blank"
                                                            className="relative h-full w-full"
                                                        >
                                                            <Image
                                                                src={`${process.env.NEXT_PUBLIC_ASSETS_URL}/community/${community.slug}/partners/${p.image}`}
                                                                fill
                                                                style={{
                                                                    objectFit:
                                                                        "contain",
                                                                }}
                                                                alt="Partner logo"
                                                            />
                                                        </Link>
                                                    </Card>
                                                );
                                            })}
                                        </CardContent>
                                    </Card>
                                );
                            })}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

const Skeleton = () => {
    const communityLinks = [
        { name: "website", url: "https://www.getriver.io" },
        { name: "newsletter", url: "https://www.getriver.io" },
    ];
    return (
        <div className="relative mt-4 min-w-[265px] md:ml-4">
            <Card className="sticky top-[80px] md:border-none md:bg-transparent">
                <CardContent className="flex animate-pulse flex-col gap-9 p-4">
                    <div className="flex flex-col gap-2">
                        <Label>Organizers</Label>
                        {[1, 2].map((_, index) => (
                            <div
                                className="flex w-full gap-4"
                                key={`organizer-host-${index}`}
                            >
                                <div className="h-8 w-8 rounded-full bg-gray-200"></div>
                            </div>
                        ))}
                    </div>
                    <div className="flex flex-col gap-2">
                        <Label>Top Hosts</Label>
                        {[1, 2, 3].map((_, index) => (
                            <div
                                className="flex w-full gap-4"
                                key={`top-host-${index}`}
                            >
                                <div className="h-8 w-8 rounded-full bg-gray-200"></div>
                            </div>
                        ))}
                    </div>

                    <div className="flex flex-col items-start gap-2">
                        <Label>More</Label>
                        <div className="flex w-2/3 flex-col gap-2">
                            {communityLinks.map((l, i) => (
                                <div
                                    className="flex justify-between"
                                    key={"more-links-" + i}
                                >
                                    <ThemedLink
                                        href={l.url}
                                        className="h-full p-0"
                                        target="_blank"
                                        variant="secondary"
                                    >
                                        {l.name}
                                    </ThemedLink>
                                </div>
                            ))}
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

const CommunityPagePeopleTab = ({
    community,
    refetch,
    hasPermissions,
}: {
    refetch?: () => void;
    community?: GetCommunity;
    hasPermissions: boolean;
}) => {
    const [mode, setMode] = useState<"view" | "edit">("view");
    if (!community) return <Skeleton />;

    if (mode === "edit" && hasPermissions) {
        return (
            <EditMode
                community={community}
                hasPermissions={hasPermissions}
                setMode={setMode}
                refetch={refetch}
            />
        );
    }

    return (
        <ViewMode
            community={community}
            hasPermissions={hasPermissions}
            setMode={setMode}
        />
    );
};

export default CommunityPagePeopleTab;
